/**
 * useDeactivateCorrection — soft-delete an mcp_corrections row.
 *
 * The table is append-only (CLAUDE.md rule 6 + RLS); "delete" means
 * setting `is_active = false`. The row stays in the audit trail. RLS
 * + immutability trigger restrict UPDATE to contractor_admin /
 * super_admin in the row's org and limit mutable fields to
 * `is_active`, `reason`, `category` (migration 20260501000009 +
 * 20260507000001).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface DeactivateVars {
  correctionId: string
}

export interface DeactivateResult {
  id:        string
  is_active: boolean
}

export function useDeactivateCorrection(): UseMutationResult<DeactivateResult, AppError, DeactivateVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<DeactivateResult, AppError, DeactivateVars>({
    mutationFn: async ({ correctionId }): Promise<DeactivateResult> => {
      const { data, error } = await supabase
        .from('mcp_corrections')
        .update({ is_active: false })
        .eq('id', correctionId)
        .select('id, is_active')
        .single()

      if (error) {
        const code = mapPgErrorCode(error.code)
        throw new AppError({
          code,
          message:     error.message,
          userMessage: userMessageFor(code, error.message),
          status:      403,
        })
      }
      return data as unknown as DeactivateResult
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mcp-corrections'] })
    },
  })
}

function mapPgErrorCode(pgCode: string | undefined): string {
  switch (pgCode) {
    case '42501':    return 'FORBIDDEN'
    case 'PGRST116': return 'NOT_FOUND'
    default:         return 'UPDATE_FAILED'
  }
}

function userMessageFor(code: string, fallback: string): string {
  switch (code) {
    case 'FORBIDDEN': return 'You do not have permission to deactivate this correction.'
    case 'NOT_FOUND': return 'This correction could not be found.'
    default:          return fallback || 'Could not deactivate the correction.'
  }
}
